import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Sparkles, Loader2, ChevronLeft, ChevronRight, ExternalLink, Volume2, Mic } from 'lucide-react';
import { apiClient } from '../services/api';
import { useMicrophone, MicState } from '../hooks/useMicrophone';
import {
  EvaluateMeaningResponse,
  EvaluateSynonymResponse,
  EvaluateAntonymResponse,
} from '../types/api';
import { PracticePhrases } from './PracticePhrases';
import { CardDetail } from './CardDetail';
import { trackPracticeStarted, trackPracticeCompleted, trackPracticeAbandoned, trackCardReviewed } from '../utils/analytics';
import { useAuth } from '../hooks/useAuth';
import { getLanguage, getMicLocale } from '../utils/language';
import { speak } from '../utils/speechUtils';

function ListenButton({ text, locale = 'en-US' }: { text: string; locale?: string }) {
  return (
    <button
      type="button"
      disabled={!text.trim()}
      onClick={() => speak(text.trim(), { lang: locale })}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
      title="Listen to your answer"
    >
      <Volume2 className="h-3.5 w-3.5" />
      Listen
    </button>
  );
}

interface SessionInfo {
  position: number;
  total: number;
  onNext: () => void;
}

interface PracticeCardProps {
  cardId: number;
  cardName: string;
  onClose: () => void;
  sessionInfo?: SessionInfo;
  isInstanceOwner?: boolean;
  onDelete?: (id: number) => void;
  onMove?: (itemId: number, data: { parent_id?: number | null; category_id?: number | null }) => Promise<void>;
  categories?: Array<{ id: number; name: string }>;
}

const TASK_FOCUS_CLASS =
  'rounded-xl border border-gray-100/90 bg-gray-50/80 p-5 sm:p-6 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.6)]';

// LANGUAGE is derived per-user at runtime
const WIZARD_STEPS = 5;

function evaluationBadgeClass(evaluation: string): string {
  const e = evaluation.trim().toLowerCase();
  if (e === 'incorrect') return 'bg-red-100 text-red-800';
  if (e.includes('partially')) return 'bg-amber-100 text-amber-900';
  if (e === 'correct') return 'bg-green-100 text-green-800';
  if (e === 'excellent') return 'bg-emerald-100 text-emerald-900';
  return 'bg-gray-100 text-gray-800';
}

function formatGrammarRoleLabel(role: string): string {
  if (!role) return role;
  return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

function AlternativesByRolePanel({
  title,
  alternativesByRole,
}: {
  title: string;
  alternativesByRole: Record<string, string[]>;
}) {
  const entries = Object.entries(alternativesByRole).filter(
    ([, words]) => Array.isArray(words) && words.length > 0
  );
  if (entries.length === 0) return null;

  return (
    <div className="border border-gray-200/90 rounded-lg p-4 bg-white/60">
      <h4 className="text-sm font-medium text-gray-600 mb-3">{title}</h4>
      <ul className="space-y-3">
        {entries.map(([role, words]) => (
          <li key={role}>
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {formatGrammarRoleLabel(role)}
            </span>
            <p className="text-sm text-gray-800 mt-1 leading-relaxed">
              {words.join(', ')}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MicButton({ micState, onStart, onStop, title }: { micState: MicState; onStart: () => void; onStop: () => void; title: string }) {
  if (micState === 'transcribing') {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Transcribing…
      </span>
    );
  }
  if (micState === 'recording') {
    return (
      <button
        type="button"
        onClick={onStop}
        className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
        title="Stop recording"
      >
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
        </span>
        Stop
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onStart}
      className="inline-flex items-center gap-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded-md transition-colors"
      title={title}
    >
      <Mic className="h-3.5 w-3.5" />
      Speak
    </button>
  );
}

export const PracticeCard: React.FC<PracticeCardProps> = ({ cardId, cardName, onClose, sessionInfo, isInstanceOwner, onDelete, onMove, categories }) => {
  const { user } = useAuth();
  const LANGUAGE = getLanguage(user?.email ?? '');
  const micLocale = getMicLocale(user?.email ?? '');
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [userMeaning, setUserMeaning] = useState('');
  const [isLoadingMeaning, setIsLoadingMeaning] = useState(false);
  const [meaningFeedback, setMeaningFeedback] = useState<EvaluateMeaningResponse | null>(null);
  const [meaningError, setMeaningError] = useState<string | null>(null);

  const [synonymCandidate, setSynonymCandidate] = useState('');
  const [isLoadingSynonym, setIsLoadingSynonym] = useState(false);
  const [synonymFeedback, setSynonymFeedback] = useState<EvaluateSynonymResponse | null>(null);
  const [synonymError, setSynonymError] = useState<string | null>(null);

  const [antonymCandidate, setAntonymCandidate] = useState('');
  const [isLoadingAntonym, setIsLoadingAntonym] = useState(false);
  const [antonymFeedback, setAntonymFeedback] = useState<EvaluateAntonymResponse | null>(null);
  const [antonymError, setAntonymError] = useState<string | null>(null);
  const [showCardDetail, setShowCardDetail] = useState(false);

  const micLang = micLocale.split('-')[0];
  const micMeaning = useMicrophone((t) => setUserMeaning((prev) => prev ? `${prev} ${t}` : t), 'definition', micLang);
  const micSynonym = useMicrophone((t) => setSynonymCandidate(t.trim()), 'synonym', micLang);
  const micAntonym = useMicrophone((t) => setAntonymCandidate(t.trim()), 'antonym', micLang);

  const hasLoggedReviewRef = useRef(false);

  useEffect(() => {
    trackPracticeStarted('card');
  }, []);

  const logReviewOnce = useCallback(async () => {
    if (hasLoggedReviewRef.current) return;
    hasLoggedReviewRef.current = true;
    try {
      const currentReview = await apiClient.getCardReview(cardId);
      const currentReviewCount = currentReview?.review_count || 0;
      const newReviewCount = currentReviewCount + 1;
      const now = new Date().toISOString();
      await apiClient.logCardReview(cardId, {
        last_reviewed_at: now,
        review_count: newReviewCount,
      });
      trackCardReviewed(cardId);
    } catch (err) {
      console.error('Error updating last_reviewed_at and review_count:', err);
    }
  }, [cardId]);

  const handleWizardClose = () => {
    const wasReviewed = hasLoggedReviewRef.current;
    setStep(1);
    setUserMeaning('');
    setMeaningFeedback(null);
    setMeaningError(null);
    setSynonymCandidate('');
    setSynonymFeedback(null);
    setSynonymError(null);
    setAntonymCandidate('');
    setAntonymFeedback(null);
    setAntonymError(null);
    hasLoggedReviewRef.current = false;
    if (wasReviewed) {
      trackPracticeCompleted('card');
    } else {
      trackPracticeAbandoned('card');
    }
    onClose();
  };

  const handleGetMeaningFeedback = async () => {
    if (!userMeaning.trim()) return;
    try {
      setIsLoadingMeaning(true);
      setMeaningFeedback(null);
      setMeaningError(null);

      const response = await apiClient.evaluateMeaning({
        word: cardName,
        user_meaning: userMeaning.trim(),
        language: LANGUAGE,
      });

      setMeaningFeedback(response);
      await logReviewOnce();
    } catch (err: any) {
      console.error('Error getting meaning feedback:', err);
      setMeaningError(err.response?.data?.detail || 'Failed to get AI feedback. Please try again.');
    } finally {
      setIsLoadingMeaning(false);
    }
  };

  const handleEvaluateSynonym = async () => {
    if (!synonymCandidate.trim()) return;
    try {
      setIsLoadingSynonym(true);
      setSynonymFeedback(null);
      setSynonymError(null);

      const response = await apiClient.evaluateSynonym({
        word: cardName,
        candidate: synonymCandidate.trim(),
        language: LANGUAGE,
        card_id: cardId,
      });

      setSynonymFeedback(response);
      await logReviewOnce();
    } catch (err: any) {
      console.error('Error evaluating synonym:', err);
      setSynonymError(err.response?.data?.detail || 'Failed to evaluate. Please try again.');
    } finally {
      setIsLoadingSynonym(false);
    }
  };

  const handleEvaluateAntonym = async () => {
    if (!antonymCandidate.trim()) return;
    try {
      setIsLoadingAntonym(true);
      setAntonymFeedback(null);
      setAntonymError(null);

      const response = await apiClient.evaluateAntonym({
        word: cardName,
        candidate: antonymCandidate.trim(),
        language: LANGUAGE,
        card_id: cardId,
      });

      setAntonymFeedback(response);
      await logReviewOnce();
    } catch (err: any) {
      console.error('Error evaluating antonym:', err);
      setAntonymError(err.response?.data?.detail || 'Failed to evaluate. Please try again.');
    } finally {
      setIsLoadingAntonym(false);
    }
  };

  const secondaryFooterBtn =
    'px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors';
  const nextBtn =
    'px-4 py-2 text-sm font-medium text-purple-800 bg-purple-50/90 border border-purple-200/80 rounded-lg hover:bg-purple-100/90 transition-colors flex items-center gap-1.5';

  const stepAriaName =
    step === 1 ? 'listening' : step === 2 ? 'phrase' : step === 3 ? 'meaning' : step === 4 ? 'synonym' : 'antonym';

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] sm:max-h-[85vh] flex flex-col">
        {/* Contextual title — subtle vs. step headings (text-lg below) */}
        <div className="flex items-start justify-between gap-4 px-6 sm:px-8 pt-6 sm:pt-7 pb-5 flex-shrink-0">
          <div className="min-w-0 pr-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h2
                className="text-base font-semibold text-gray-800 tracking-tight leading-snug break-words"
                title={`Practice · ${cardName}`}
              >
                Practice<span className="text-gray-300 font-normal mx-1.5" aria-hidden>·</span>
                <button
                  type="button"
                  onClick={() => setShowCardDetail(true)}
                  className="text-blue-600 underline hover:text-blue-800 transition-colors"
                  title="View card details"
                >{cardName}</button>
                {sessionInfo && (
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    {sessionInfo.position} / {sessionInfo.total}
                  </span>
                )}
              </h2>
              <button
                type="button"
                onClick={() => speak(cardName, { lang: micLocale })}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors flex-shrink-0"
                title="Hear pronunciation"
              >
                <Volume2 className="h-4 w-4" />
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleWizardClose}
            className="p-2 -mr-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 text-gray-500 hover:text-gray-700"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Minimal step indicator */}
        <div
          className="flex justify-center items-center gap-2 sm:gap-3 px-6 pb-6 text-sm border-b border-gray-100/90 flex-wrap"
          role="status"
          aria-label={`Step ${step} of ${WIZARD_STEPS}: ${stepAriaName}`}
        >
          {(['Listening', 'Phrase', 'Define', 'Synonym', 'Antonym'] as const).map((label, i) => {
            const n = (i + 1) as 1 | 2 | 3 | 4 | 5;
            const isActive = step === n;
            return (
              <React.Fragment key={label}>
                {i > 0 && (
                  <span className="text-gray-300 select-none" aria-hidden>
                    ·
                  </span>
                )}
                <span
                  className={isActive ? 'font-semibold text-gray-900' : 'font-normal text-gray-400'}
                  title={
                    label === 'Listening'
                      ? 'Hear the word used in real YouTube videos'
                      : label === 'Phrase'
                        ? 'Write and refine a phrase using this word'
                        : label === 'Define'
                          ? 'Explain the meaning in your own words'
                          : label === 'Synonym'
                            ? 'Name a synonym'
                            : 'Name an antonym'
                  }
                >
                  {label}
                </span>
              </React.Fragment>
            );
          })}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className={step !== 1 ? 'hidden' : 'block px-6 sm:px-8 pt-8 pb-10'}>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-2">
              Hear it in context
            </h3>
            <p className="text-sm text-gray-500 mb-8">
              Listen to how native speakers use{' '}
              <span className="font-medium text-gray-700">&quot;{cardName}&quot;</span> in real videos.
            </p>

            <div className={TASK_FOCUS_CLASS}>
              <div className="flex flex-col items-center gap-5 py-4">
                <p className="text-base text-gray-600 text-center leading-relaxed">
                  YouGlish finds YouTube clips where{' '}
                  <span className="font-semibold text-gray-900">&quot;{cardName}&quot;</span> is spoken
                  naturally in context.
                </p>
                <a
                  href={`https://youglish.com/pronounce/${encodeURIComponent(cardName)}/english`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-3 text-[15px] font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 transition-all shadow-sm"
                >
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  Open on YouGlish
                </a>
              </div>
            </div>
          </div>

          <div className={step !== 2 ? 'hidden' : 'block px-6 sm:px-8 pt-8 pb-10'}>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-6">
              Write a phrase
            </h3>

            <PracticePhrases
              variant="embedded"
              cardId={cardId}
              cardName={cardName}
              onClose={handleWizardClose}
              suppressReviewLogging
              onSessionFeedbackSuccess={logReviewOnce}
              inputLabel="Your sentence"
              inputPlaceholder="Write a sentence…"
              embeddedTaskClassName={TASK_FOCUS_CLASS}
              embeddedInstruction={
                <p className="text-base text-gray-600 leading-relaxed">
                  Create a sentence using{' '}
                  <span className="font-semibold text-gray-900">&quot;{cardName}&quot;</span>.
                </p>
              }
            />
          </div>

          <div className={step !== 3 ? 'hidden' : 'block px-6 sm:px-8 pt-8 pb-10 space-y-6'}>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-6">
              Define the meaning
            </h3>

            <div className={TASK_FOCUS_CLASS}>
              <div className="space-y-5">
                <p className="text-base text-gray-600 leading-relaxed">
                  Define{' '}
                  <span className="font-semibold text-gray-900">&quot;{cardName}&quot;</span> in your
                  own words.
                </p>

                {meaningError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{meaningError}</div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="practice-card-definition" className="text-sm font-medium text-gray-700">
                      Your definition
                    </label>
                    <div className="flex items-center gap-2">
                      <MicButton micState={micMeaning.micState} onStart={micMeaning.startRecording} onStop={micMeaning.stopRecording} title="Speak your definition" />
                      <ListenButton text={userMeaning} locale={micLocale} />
                    </div>
                  </div>
                  {micMeaning.micError && (
                    <p className="text-xs text-red-500 mb-1">{micMeaning.micError}</p>
                  )}
                  <textarea
                    id="practice-card-definition"
                    value={userMeaning}
                    onChange={(e) => setUserMeaning(e.target.value)}
                    placeholder="Write or speak your definition…"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/80 focus:border-purple-300 resize-none bg-white/90"
                    rows={4}
                  />
                </div>

                <button
                  type="button"
                  onClick={handleGetMeaningFeedback}
                  disabled={isLoadingMeaning || !userMeaning.trim()}
                  className="w-full px-4 py-3 text-[15px] font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-sm"
                >
                  {isLoadingMeaning ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Getting AI Feedback...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 shrink-0" />
                      <span>Get AI Feedback</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {meaningFeedback && (
              <div className="space-y-4 p-4 bg-gradient-to-br from-purple-50/90 to-indigo-50/80 border border-purple-100 rounded-xl">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-sm text-gray-600 font-medium">Evaluation:</span>
                  <span
                    className={`px-3 py-1 rounded-md text-sm font-semibold ${evaluationBadgeClass(meaningFeedback.evaluation)}`}
                  >
                    {meaningFeedback.evaluation}
                  </span>
                </div>
                {meaningFeedback.feedback?.trim() && (
                  <div className="text-sm text-gray-800 bg-white/90 border border-purple-100/80 rounded-lg p-3 leading-relaxed">
                    {meaningFeedback.feedback}
                  </div>
                )}
                {meaningFeedback.refined_meaning?.trim() && (
                  <div className="border border-gray-200/90 rounded-lg p-4 bg-white/60">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Refined meaning</h4>
                    <p className="text-sm text-gray-700 break-words leading-relaxed">
                      {meaningFeedback.refined_meaning}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={step !== 4 ? 'hidden' : 'block px-6 sm:px-8 pt-8 pb-10 space-y-6'}>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-6">
              Synonym
            </h3>

            <div className={TASK_FOCUS_CLASS}>
              <div className="space-y-5">
                <p className="text-base text-gray-600 leading-relaxed">
                  Type a word that means the same as{' '}
                  <span className="font-semibold text-gray-900">&quot;{cardName}&quot;</span>{' '}
                  (for at least one usual sense).
                </p>

                {synonymError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{synonymError}</div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="practice-card-synonym" className="text-sm font-medium text-gray-700">
                      Your synonym
                    </label>
                    <div className="flex items-center gap-2">
                      <MicButton micState={micSynonym.micState} onStart={micSynonym.startRecording} onStop={micSynonym.stopRecording} title="Speak a synonym" />
                      <ListenButton text={synonymCandidate} locale={micLocale} />
                    </div>
                  </div>
                  {micSynonym.micError && (
                    <p className="text-xs text-red-500 mb-1">{micSynonym.micError}</p>
                  )}
                  <input
                    id="practice-card-synonym"
                    type="text"
                    value={synonymCandidate}
                    onChange={(e) => setSynonymCandidate(e.target.value)}
                    placeholder="Type or speak a synonym…"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/80 focus:border-purple-300 bg-white/90"
                    autoComplete="off"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleEvaluateSynonym}
                  disabled={isLoadingSynonym || !synonymCandidate.trim()}
                  className="w-full px-4 py-3 text-[15px] font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-sm"
                >
                  {isLoadingSynonym ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Checking…</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 shrink-0" />
                      <span>Check synonym</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {synonymFeedback && (
              <div className="space-y-4 p-4 bg-gradient-to-br from-purple-50/90 to-indigo-50/80 border border-purple-100 rounded-xl">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-sm text-gray-600 font-medium">Result:</span>
                  <span
                    className={`px-3 py-1 rounded-md text-sm font-semibold ${
                      synonymFeedback.is_synonym
                        ? 'bg-green-100 text-green-800'
                        : 'bg-amber-100 text-amber-900'
                    }`}
                  >
                    {synonymFeedback.is_synonym ? 'Good match' : 'Not a match'}
                  </span>
                </div>
                {synonymFeedback.is_synonym && synonymFeedback.grammar_roles.length > 0 && (
                  <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-600">Grammar Role(s): </span>
                    {synonymFeedback.grammar_roles.map(formatGrammarRoleLabel).join(', ')}
                  </div>
                )}
                {!synonymFeedback.is_synonym && synonymFeedback.feedback?.trim() && (
                  <div className="text-sm text-gray-800 bg-white/90 border border-purple-100/80 rounded-lg p-3 leading-relaxed">
                    {synonymFeedback.feedback}
                  </div>
                )}
                <AlternativesByRolePanel
                  title="Other synonyms"
                  alternativesByRole={synonymFeedback.alternatives_by_role}
                />
              </div>
            )}
          </div>

          <div className={step !== 5 ? 'hidden' : 'block px-6 sm:px-8 pt-8 pb-10 space-y-6'}>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight mb-6">
              Antonym
            </h3>

            <div className={TASK_FOCUS_CLASS}>
              <div className="space-y-5">
                <p className="text-base text-gray-600 leading-relaxed">
                  Type a word that means the opposite of{' '}
                  <span className="font-semibold text-gray-900">&quot;{cardName}&quot;</span>{' '}
                  (for at least one usual sense).
                </p>

                {antonymError && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-md text-sm">{antonymError}</div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label htmlFor="practice-card-antonym" className="text-sm font-medium text-gray-700">
                      Your antonym
                    </label>
                    <div className="flex items-center gap-2">
                      <MicButton micState={micAntonym.micState} onStart={micAntonym.startRecording} onStop={micAntonym.stopRecording} title="Speak an antonym" />
                      <ListenButton text={antonymCandidate} locale={micLocale} />
                    </div>
                  </div>
                  {micAntonym.micError && (
                    <p className="text-xs text-red-500 mb-1">{micAntonym.micError}</p>
                  )}
                  <input
                    id="practice-card-antonym"
                    type="text"
                    value={antonymCandidate}
                    onChange={(e) => setAntonymCandidate(e.target.value)}
                    placeholder="Type or speak an antonym…"
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500/80 focus:border-purple-300 bg-white/90"
                    autoComplete="off"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleEvaluateAntonym}
                  disabled={isLoadingAntonym || !antonymCandidate.trim()}
                  className="w-full px-4 py-3 text-[15px] font-medium bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-md hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-sm"
                >
                  {isLoadingAntonym ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Checking…</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-5 w-5 shrink-0" />
                      <span>Check antonym</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {antonymFeedback && (
              <div className="space-y-4 p-4 bg-gradient-to-br from-purple-50/90 to-indigo-50/80 border border-purple-100 rounded-xl">
                <div className="flex items-center flex-wrap gap-2">
                  <span className="text-sm text-gray-600 font-medium">Result:</span>
                  <span
                    className={`px-3 py-1 rounded-md text-sm font-semibold ${
                      antonymFeedback.is_antonym
                        ? 'bg-green-100 text-green-800'
                        : 'bg-amber-100 text-amber-900'
                    }`}
                  >
                    {antonymFeedback.is_antonym ? 'Good match' : 'Not a match'}
                  </span>
                </div>
                {antonymFeedback.is_antonym && antonymFeedback.grammar_roles.length > 0 && (
                  <div className="text-sm text-gray-700">
                    <span className="font-medium text-gray-600">Grammar Role(s): </span>
                    {antonymFeedback.grammar_roles.map(formatGrammarRoleLabel).join(', ')}
                  </div>
                )}
                {!antonymFeedback.is_antonym && antonymFeedback.feedback?.trim() && (
                  <div className="text-sm text-gray-800 bg-white/90 border border-purple-100/80 rounded-lg p-3 leading-relaxed">
                    {antonymFeedback.feedback}
                  </div>
                )}
                <AlternativesByRolePanel
                  title="Other antonyms"
                  alternativesByRole={antonymFeedback.alternatives_by_role}
                />
              </div>
            )}
          </div>
        </div>

        {/* Secondary navigation — visually lighter than the task area */}
        <div className="flex items-center justify-end gap-2 sm:gap-3 px-6 sm:px-8 py-4 border-t border-gray-100 bg-white flex-shrink-0 flex-wrap">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((s) => (s > 1 ? ((s - 1) as 1 | 2 | 3 | 4 | 5) : s))}
              className={`${secondaryFooterBtn} mr-auto`}
            >
              <span className="inline-flex items-center gap-1.5">
                <ChevronLeft className="h-4 w-4 opacity-70" />
                Back
              </span>
            </button>
          )}
          {step === 1 && (
            <button type="button" onClick={() => setStep(2)} className={nextBtn}>
              <span>Next</span>
              <ChevronRight className="h-4 w-4 opacity-80" />
            </button>
          )}
          {step === 2 && (
            <button type="button" onClick={() => setStep(3)} className={nextBtn}>
              <span>Next</span>
              <ChevronRight className="h-4 w-4 opacity-80" />
            </button>
          )}
          {step === 3 && (
            <button type="button" onClick={() => setStep(4)} className={nextBtn}>
              <span>Next</span>
              <ChevronRight className="h-4 w-4 opacity-80" />
            </button>
          )}
          {step === 4 && (
            <button type="button" onClick={() => setStep(5)} className={nextBtn}>
              <span>Next</span>
              <ChevronRight className="h-4 w-4 opacity-80" />
            </button>
          )}
          {sessionInfo && (
            <button
              type="button"
              onClick={sessionInfo.onNext}
              className={nextBtn}
            >
              <span>Next Card</span>
              <ChevronRight className="h-4 w-4 opacity-80" />
            </button>
          )}
          <button type="button" onClick={handleWizardClose} className={secondaryFooterBtn}>
            Close
          </button>
        </div>
      </div>
    </div>

    {showCardDetail && (
      <CardDetail
        cardId={cardId}
        onClose={() => setShowCardDetail(false)}
        onSave={() => {}}
        isInstanceOwner={isInstanceOwner}
        onDelete={onDelete}
        onMove={onMove}
        categories={categories}
      />
    )}
    </>
  );
};
